// MessageInput.js
import { Button } from "antd";
import { Modal } from "react-bootstrap";
import { useState, useRef } from "react";
import { post } from "../../../services/ApiService";
import toast, { Toaster } from "react-hot-toast";
const MessageInput = ({
  order,
  message,
  attachments,
  setMessage,
  setAttachments,
  textareaRef,
  handleSendMessage,
  isSending,
  onTemplateOpen,
  replaceTemplates,
  ticket,
  handleCloseTicket,
  hasUnreadMessage,
  setHasUnreadMessage,
  amazonMessagingInfo,
  canSendMsg,
}) => {
  const [isSubmit, setIsSubmit] = useState(false);
  const [isNoResponse, setIsNoResponse] = useState(false);
  const fileInputRef = useRef(null);
  const disableAmazonSend =
    order?.platform === "Amazon" && amazonMessagingInfo?.canSendMessage === false;
  const amazonDisabledReason =
    order?.platform === "Amazon" && disableAmazonSend
      ? amazonMessagingInfo?.disabledReason ||
        "Amazon messaging is not available for this order."
      : null;

  const handleChange = (e) => {
    setMessage(replaceTemplates(e.target.value));
  };

  const handleKeyUp = (e) => {
    if (e.key === "{" || e.key === "}") {
      const cursorPos = textareaRef.current.selectionStart;
      setTimeout(() => {
        textareaRef.current.setSelectionRange(cursorPos, cursorPos);
      }, 0);
    }
  };

  const handleNoResponse = async () => {
    setIsNoResponse(true);
    // Opened before the await: browsers only allow pop-ups during the click itself.
    const amazonPopup =
      order?.platform === "Amazon"
        ? window.open("", "amazonResolveCase", "popup,width=560,height=760")
        : null;
    const sender = order?.buyerInfo?.username;
    try {
      const body = {
        sender,
        ticketId: ticket?._id,
      };
      const res = await post("api/messages/mark-all-read", body);
      setIsNoResponse(false);
      if (amazonPopup) {
        if (res?.success && res.amazonResolveUrl) {
          amazonPopup.location.href = res.amazonResolveUrl;
        } else {
          amazonPopup.close();
        }
      }
      if (res?.success) {
        setHasUnreadMessage(false);
        if (ticket) {
          handleCloseTicket(res.ticket);
        }
        if (res.amazonResolveUrl && !amazonPopup) {
          toast.error("Pop-up blocked: allow pop-ups for this site, then click No response needed again.");
        } else {
          toast.success(
            res.amazonResolveUrl
              ? "Marked as read. Finish \"No response needed\" in the Amazon window (sign in to Seller Central if asked)."
              : res.message
          );
        }
        window.dispatchEvent(new Event("chat_notifications_updated"));
      } else {
        toast.error("Failed to mark read:", res?.error);
      }
    } catch (error) {
      amazonPopup?.close();
      console.error("Error while read:", error);
      toast.error("Failed to mark read:", error);
      setIsNoResponse(false);
    }
  };

  // Handle file selected from input
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validImages = files.filter((file) =>
      ["image/jpeg", "image/png"].includes(file.type)
    );

    if (validImages.length !== files.length) {
      toast.error("Only JPG and PNG images are allowed");
    }

    setAttachments((prev) => [...prev, ...validImages]);
    e.target.value = null;
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Trigger file input click when attachment button clicked
  const handleAttachmentClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="chat-panel__composer card-footer border-0 bg-transparent">
      {amazonDisabledReason && (
        <div className="alert alert-warning d-flex align-items-start gap-2 py-2 mb-2" role="alert">
          <i className="fa fa-exclamation-triangle mt-1 flex-shrink-0" aria-hidden="true" />
          <span className="fs-13">{amazonDisabledReason}</span>
        </div>
      )}
      <textarea
        ref={textareaRef}
        value={message}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        className="form-control"
        rows={5}
        style={{
          width: "100%",
          overflow: "hidden",
          resize: "none",
        }}
        placeholder="Type your message here..."
        disabled={disableAmazonSend}
      />
      <div className="col-12 text-end">
        {canSendMsg && (
          <button
            type="button"
            className="btn btn-warning btn-sm m-4"
            onClick={() => handleNoResponse()}
            disabled={isNoResponse}
          >
            {isNoResponse ? (
              <>
                Please wait...{" "}
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
              </>
            ) : (
              <>
                No response needed{" "}
                <i className="fa fa-check-circle" aria-hidden="true" />
              </>
            )}
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary btn-sm m-4"
          onClick={onTemplateOpen}
        >
          Templates <i className="fa fa-file-text" aria-hidden="true" />
        </button>
        {canSendMsg && (
          <>
            <button
              type="button"
              className="btn btn-primary btn-sm m-4"
              onClick={handleSendMessage}
              disabled={isSending || disableAmazonSend}
              loading={isSending}
            >
              Send{" "}
              {isSending ? (
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
              ) : (
                <i className="fa fa-paper-plane" aria-hidden="true"></i>
              )}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm m-4"
              onClick={handleAttachmentClick}
              disabled={isSending}
            >
              <i className="fa fa-paperclip" aria-hidden="true"></i>
            </button>
            <input
              type="file"
              accept="image/png, image/jpeg"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </>
        )}
      </div>
      {canSendMsg && (
        <div className="col-12 mt-4 d-flex flex-wrap gap-2">
          {attachments.map((file, index) => (
            <div className="attachment-box position-relative" key={index}>
              <img
                src={URL.createObjectURL(file)}
                alt={`attachment-${index}`}
                width={100}
                height={100}
              />
              <span
                className="remove-btn"
                onClick={() => handleRemoveAttachment(index)}
              >
                ×
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageInput;
